import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, ChevronDown, Clock } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Entry({ e }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border border-slate-200 rounded-lg" data-testid={`mail-log-entry-${e.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50">
        <span className="text-xs text-slate-500 w-36 shrink-0">{new Date(e.created_at).toLocaleString("de-DE")}</span>
        <span className="flex-1 min-w-0">
          <span className="block font-medium text-slate-800 truncate">{e.subject}</span>
          <span className="block text-xs text-slate-500 truncate">
            an {e.to}{e.redirected_to ? ` (Testmodus → ${e.redirected_to})` : ""} · {e.template_name} · {e.sent_by}
          </span>
        </span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (e.html
        ? <iframe title="Mail-Inhalt" sandbox="" srcDoc={e.html} className="w-full h-72 border-t border-slate-200 bg-white rounded-b-lg" />
        : <p className="px-3 py-2 text-xs text-slate-400 border-t border-slate-200">Inhalt wurde bei älteren Mails nicht gespeichert.</p>)}
    </li>
  );
}

export function MailLogSection({ customerId, customerName, reloadKey, kind = "customers" }) {
  const [logs, setLogs] = useState([]);
  const [year, setYear] = useState("alle");
  useEffect(() => {
    api.get(`/${kind}/${customerId}/email-log`).then((r) => setLogs(r.data)).catch(() => {});
  }, [customerId, reloadKey, kind]);

  const years = [...new Set(logs.map((e) => (e.created_at || "").slice(0, 4)).filter(Boolean))].sort().reverse();
  const shown = year === "alle" ? logs : logs.filter((e) => (e.created_at || "").startsWith(`${year}-`));

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/${kind}/${customerId}/email-log.pdf`, { params: year === "alle" ? {} : { year }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Mailprotokoll_${customerName || "Kontakt"}_${year}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="section-mail-log">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Clock size={14} /> Mail-Protokoll ({shown.length})</h2>
        <div className="flex items-center gap-2">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="h-9 w-32" data-testid="mail-log-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle Jahre</SelectItem>
              {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="mail-log-pdf" data-backend={BACKEND ? "1" : "0"}><FileDown size={15} className="mr-1.5" /> Als PDF</Button>
        </div>
      </div>
      {shown.length === 0
        ? <p className="text-sm text-slate-400" data-testid="mail-log-empty">Keine Mails im gewählten Zeitraum.</p>
        : <ul className="space-y-2">{shown.map((e) => <Entry key={e.id} e={e} />)}</ul>}
    </section>
  );
}
